/**
 * AI Sections Test Page
 *
 * Demo page for testing the AI Sections feature in the preprod environment.
 * Shows how to use the AISections component with various configurations.
 * Uses the shared AppContext for domain/endpoint selection.
 */

import { AnalyticsProvider } from '@inboundx/shared/analytics';
import {
    type AISectionWithMeta,
    FetchNetworkAdapter,
    listAISectionsForDomain,
} from '@inboundx/shared/platform';
import { type AISectionConfig, AISections } from '@inboundx/shared/ui';
import { DisplayController } from '@inboundx/shared/widget';
import { AppWindow, Database, Eye, EyeOff, FileCode, Settings } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ConfigurableStorageAdapter } from '../adapters/ConfigurableStorageAdapter';
import { AuthButton } from '../components/AuthButton';
import { ClientConfigProvider } from '../components/ClientConfigProvider';
import ConfigPanel from '../components/ConfigPanel';
import LanguagePicker, { type SupportedLanguage } from '../components/LanguagePicker';
import { LanguageSyncFromConfig } from '../components/LanguageSyncFromConfig';
import { getConfig, getConfigSummary } from '../config';
import { useAppContext } from '../contexts/AppContext';

// Sample configurations for testing
const SAMPLE_CONFIGS: Record<string, AISectionConfig> = {
    'minimal-cards': {
        title: '', // No title - mimics the screenshot style
        questions: ['Does the answer is personalized for each lead?', 'How to train the agent?'],
        styling: {
            primaryColor: '#6b7280', // Gray sparkle icon
            buttonBackground: '#FFFFFF',
            buttonTextColor: '#1f2937',
            titleColor: '#1f2937',
            gap: '12px',
            buttonFontSize: '15px',
            buttonFontWeight: '500',
        },
        customCSS: `
            .ai-sections-container {
                background: transparent !important;
            }
            .ai-sections-button {
                border: 1px solid #e5e7eb !important;
                box-shadow: none !important;
                background: #f9fafb !important;
            }
            .ai-sections-button:hover {
                background: #f3f4f6 !important;
                border-color: #d1d5db !important;
            }
            .ai-sections-arrow {
                opacity: 0.4 !important;
            }
        `,
    },
    pricing: {
        title: 'Ask us about pricing',
        questions: [
            'What plan fits my needs?',
            'Is there a free trial?',
            'How does billing work?',
            'Do you offer discounts for startups?',
        ],
        styling: {
            primaryColor: '#006967',
            buttonBackground: '#FFFFFF',
            buttonTextColor: '#1a1a1a',
            titleColor: '#003E3F',
            gap: '12px',
        },
    },
    features: {
        title: 'Learn about our features',
        questions: [
            'What integrations do you support?',
            'How does the AI work?',
            'Can I customize the chatbot?',
        ],
        styling: {
            primaryColor: '#0A42C3',
            buttonBackground: '#F0F4FF',
            buttonTextColor: '#0A42C3',
            titleColor: '#1a1a1a',
            gap: '16px',
        },
    },
    support: {
        title: 'Need help?',
        questions: ['How do I get started?', 'Where can I find documentation?'],
        styling: {
            primaryColor: '#7C3AED',
            buttonBackground: '#F5F3FF',
            buttonTextColor: '#5B21B6',
            titleColor: '#1a1a1a',
            gap: '12px',
        },
    },
    'with-search': {
        title: 'Questions + Search Bar',
        questions: ['What does your product do?', 'How much does it cost?'],
        showSearchBar: true,
        styling: {
            primaryColor: '#006967',
            buttonBackground: '#FFFFFF',
            buttonTextColor: '#1a1a1a',
            titleColor: '#003E3F',
            gap: '12px',
        },
    },
    'search-only': {
        title: 'Ask us anything',
        questions: [],
        showSearchBar: true,
        styling: {
            primaryColor: '#0A42C3',
            buttonBackground: '#FFFFFF',
            buttonTextColor: '#1a1a1a',
            titleColor: '#1a1a1a',
            gap: '12px',
        },
    },
    'unhide-parent': {
        title: 'Unhide Parent Demo (display:none)',
        questions: ['What does your product do?', 'How much does it cost?'],
        showSearchBar: true,
        styling: {
            primaryColor: '#006967',
            buttonBackground: '#FFFFFF',
            buttonTextColor: '#1a1a1a',
            titleColor: '#003E3F',
            gap: '12px',
            unhideParent: true,
        },
    },
    'search-top': {
        title: '',
        questions: [
            'Quels sont les services proposés par Bulldozer ?',
            'Comment fonctionne votre modèle de collectif ?',
        ],
        showSearchBar: true,
        searchBarPlaceholder: {
            en: 'Ask your questions about Bulldozer',
            fr: 'Posez vos questions sur Bulldozer',
        },
        styling: {
            primaryColor: '#000000',
            buttonBackground: '#FFFFFF',
            buttonTextColor: '#000000',
            titleColor: '#202603',
            gap: '12px',
            buttonFontSize: '16px',
            buttonFontWeight: '400',
            iconSize: 20,
            arrowSize: 24,
            searchBarPosition: 'top',
        },
        customCSS: `
            .ai-sections-container {
                background: transparent !important;
                padding: 12px !important;
            }
            .ai-sections-content {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                gap: 20px !important;
                padding: 0 !important;
            }
            .ai-sections-content > div {
                align-items: center !important;
            }
            .ai-sections-search-bar {
                background: linear-gradient(104.47deg, #f5f5f5 2.44%, #ffffff 101.32%) !important;
                border: 1px solid #e3e3e3 !important;
                border-radius: 12px !important;
                width: 722px !important;
                max-width: 100% !important;
                height: 120px !important;
                padding: 12px !important;
            }
            .ai-sections-button {
                border: 1px solid #e3e3e3 !important;
                border-radius: 100px !important;
                padding: 8px 12px !important;
                height: 38px !important;
                box-shadow: none !important;
            }
            .ai-sections-button-text {
                font-family: 'DM Sans Variable', sans-serif !important;
                font-size: 16px !important;
                font-weight: 400 !important;
            }
        `,
    },
};

// Config source type for distinguishing sample vs database configs
type ConfigSource = 'sample' | 'database';

interface ConfigOption {
    key: string;
    label: string;
    source: ConfigSource;
    config: AISectionConfig;
    enabled?: boolean;
}

export default function AISectionsTestPage() {
    const {
        selectedClient,
        selectedEndpoint,
        customEndpoint,
        useCustomEndpoint,
        availableClients,
    } = useAppContext();

    const [selectedConfigKey, setSelectedConfigKey] = useState<string>('sample:pricing');
    const [clickedQuestions, setClickedQuestions] = useState<string[]>([]);
    const [showConfig, setShowConfig] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('en');
    const [key, setKey] = useState(0);
    const widgetContainerRef = useRef<HTMLDivElement>(null);
    const [dbSections, setDbSections] = useState<AISectionWithMeta[]>([]);
    const [isLoadingDbSections, setIsLoadingDbSections] = useState(false);
    const [layoutMode, setLayoutMode] = useState<'inline' | 'stacked'>('stacked');
    const [barForceHidden, setBarForceHidden] = useState(false);
    const unhideParentRef = useRef<HTMLDivElement>(null);

    const [networkAdapter] = useState(new FetchNetworkAdapter());

    // Fetch database sections when client or language changes
    useEffect(() => {
        if (!selectedClient?.domain) {
            setDbSections([]);
            return;
        }

        const fetchDbSections = async () => {
            setIsLoadingDbSections(true);
            try {
                const sections = await listAISectionsForDomain(
                    selectedClient.domain,
                    selectedLanguage
                );
                setDbSections(sections);
            } catch (error) {
                console.error('Failed to fetch DB sections:', error);
                setDbSections([]);
            } finally {
                setIsLoadingDbSections(false);
            }
        };

        fetchDbSections();
    }, [selectedClient?.domain, selectedLanguage]);

    // Build combined config options (DB first, then samples)
    const configOptions: ConfigOption[] = useMemo(() => {
        const options: ConfigOption[] = [];

        // Add database configs first
        dbSections.forEach((section) => {
            options.push({
                key: `db:${section.sectionId}`,
                label: section.sectionId,
                source: 'database',
                config: section,
                enabled: section.enabled,
            });
        });

        // Add sample configs
        Object.entries(SAMPLE_CONFIGS).forEach(([configKey, config]) => {
            options.push({
                key: `sample:${configKey}`,
                label: configKey,
                source: 'sample',
                config,
            });
        });

        return options;
    }, [dbSections]);

    // Get current selected config
    const currentOption = configOptions.find((opt) => opt.key === selectedConfigKey);
    const baseConfig = currentOption?.config || SAMPLE_CONFIGS.pricing;

    // Apply layout mode override
    const currentConfig: AISectionConfig = {
        ...baseConfig,
        styling: {
            ...baseConfig.styling,
            layout: layoutMode,
        },
    };

    // Get current API URL
    const getCurrentApiUrl = useCallback(() => {
        if (useCustomEndpoint && customEndpoint) {
            return customEndpoint;
        }
        return selectedEndpoint?.url || '';
    }, [useCustomEndpoint, customEndpoint, selectedEndpoint?.url]);

    // Create storage adapter
    const storageAdapter = useMemo(
        () => new ConfigurableStorageAdapter(getCurrentApiUrl),
        [getCurrentApiUrl]
    );

    const handleQuestionClick = (question: string) => {
        setClickedQuestions((prev) => [
            ...prev,
            `[${new Date().toLocaleTimeString()}] ${question}`,
        ]);
    };

    const handleConfigChange = () => {
        setKey((prev) => prev + 1);
    };

    const toggleBarVisibility = () => {
        const newHidden = !barForceHidden;
        setBarForceHidden(newHidden);
        // Hide/show the widget bar container
        if (widgetContainerRef.current) {
            widgetContainerRef.current.style.display = newHidden ? 'none' : '';
        }
        // Dispatch event so AI sections react
        window.dispatchEvent(
            new CustomEvent(newHidden ? 'inboundx_bar_hidden' : 'inboundx_bar_visible')
        );
    };

    // Show message if no client is selected
    if (!selectedClient || availableClients.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
                    <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No Client Selected</h2>
                    <p className="text-gray-600 mb-4">
                        Please select a client from the main app to test AI Sections.
                    </p>
                    <Button onClick={() => setShowConfig(true)}>
                        <Settings className="mr-2 h-4 w-4" />
                        Open Settings
                    </Button>
                </div>
                <ConfigPanel
                    isOpen={showConfig}
                    onClose={() => setShowConfig(false)}
                    onConfigChange={handleConfigChange}
                    apiHealthStatus={{}}
                    configSummary={getConfigSummary()}
                    onRefreshHealthChecks={() => Promise.resolve()}
                    isRefreshingHealth={false}
                />
            </div>
        );
    }

    const currentApiUrl = getCurrentApiUrl();

    return (
        <ClientConfigProvider
            client={selectedClient}
            apiUrl={currentApiUrl}
            apiKey={getConfig().apiKey}
        >
            <LanguageSyncFromConfig onLanguageDetected={setSelectedLanguage} />
            <AnalyticsProvider>
                <div className="min-h-screen bg-gray-50 py-12 px-4">
                    {/* Top Controls */}
                    <div className="fixed top-6 right-6 z-[999] flex items-center gap-3">
                        <LanguagePicker
                            selectedLanguage={selectedLanguage}
                            onLanguageChange={setSelectedLanguage}
                            className="bg-black/70 backdrop-blur-sm rounded-lg border border-white/20"
                        />
                        <AuthButton />
                        <Button
                            onClick={toggleBarVisibility}
                            size="icon"
                            className={`w-12 h-12 rounded-full border-2 shadow-lg hover:scale-110 transition-all duration-300 ${
                                barForceHidden
                                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                                    : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                            }`}
                            title={
                                barForceHidden
                                    ? 'Bar is HIDDEN (click to show)'
                                    : 'Bar is VISIBLE (click to hide)'
                            }
                        >
                            {barForceHidden ? (
                                <EyeOff className="h-5 w-5" />
                            ) : (
                                <Eye className="h-5 w-5" />
                            )}
                        </Button>
                        <Button
                            onClick={() => setShowConfig(true)}
                            size="icon"
                            className="w-12 h-12 rounded-full border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-lg hover:scale-110 transition-all duration-300"
                            title="Settings"
                        >
                            <Settings className="h-5 w-5" />
                        </Button>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        {/* Header */}
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                AI Sections Demo
                            </h1>
                            <p className="text-gray-600">
                                Testing with{' '}
                                <span className="font-medium text-blue-600">
                                    {selectedClient.name}
                                </span>{' '}
                                ({selectedClient.domain})
                            </p>
                            <p className="text-gray-500 text-sm mt-1">
                                Questions will trigger the Rose widget when clicked.
                            </p>
                        </div>

                        {/* Configuration Selector */}
                        <div className="mb-8 bg-white rounded-lg shadow p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                Select Configuration
                            </h2>

                            {/* Database Sections */}
                            {(dbSections.length > 0 || isLoadingDbSections) && (
                                <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Database className="h-4 w-4 text-green-600" />
                                        <span className="text-sm font-medium text-gray-600">
                                            Database ({selectedClient?.domain})
                                        </span>
                                        {isLoadingDbSections && (
                                            <span className="text-xs text-gray-400">
                                                Loading...
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {configOptions
                                            .filter((opt) => opt.source === 'database')
                                            .map((opt) => (
                                                <button
                                                    type="button"
                                                    key={opt.key}
                                                    onClick={() => setSelectedConfigKey(opt.key)}
                                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                                        selectedConfigKey === opt.key
                                                            ? 'bg-green-600 text-white'
                                                            : opt.enabled === false
                                                              ? 'bg-gray-100 text-gray-400 line-through'
                                                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                                                    }`}
                                                >
                                                    {opt.label}
                                                    {opt.enabled === false && (
                                                        <span className="text-xs">(disabled)</span>
                                                    )}
                                                </button>
                                            ))}
                                        {dbSections.length === 0 && !isLoadingDbSections && (
                                            <span className="text-sm text-gray-400 italic">
                                                No sections in database
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Sample Configs */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <FileCode className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium text-gray-600">
                                        Sample Configs
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {configOptions
                                        .filter((opt) => opt.source === 'sample')
                                        .map((opt) => (
                                            <button
                                                type="button"
                                                key={opt.key}
                                                onClick={() => setSelectedConfigKey(opt.key)}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                    selectedConfigKey === opt.key
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                                                }`}
                                            >
                                                {opt.label.charAt(0).toUpperCase() +
                                                    opt.label.slice(1)}
                                            </button>
                                        ))}
                                </div>
                            </div>

                            {/* Layout Toggle */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm font-medium text-gray-600">
                                        Layout:
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setLayoutMode('stacked')}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                layoutMode === 'stacked'
                                                    ? 'bg-gray-800 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            Stacked (one per line)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setLayoutMode('inline')}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                layoutMode === 'inline'
                                                    ? 'bg-gray-800 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            Inline (wrapped)
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Website Agent Demo - Full Card Layout (only for minimal-cards sample) */}
                        {selectedConfigKey === 'sample:minimal-cards' && (
                            <div className="mb-8 rounded-2xl shadow-lg overflow-hidden bg-white border border-gray-200">
                                <div className="bg-gray-100 px-6 py-3 border-b">
                                    <h3 className="text-sm font-medium text-gray-700">
                                        Website Agent Card Demo (Screenshot Style)
                                    </h3>
                                </div>
                                <div className="p-10">
                                    {/* Icon */}
                                    <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center mb-8">
                                        <AppWindow className="w-7 h-7 text-white" />
                                    </div>

                                    {/* Title */}
                                    <h2 className="text-4xl font-semibold text-gray-900 mb-4">
                                        Website Agent
                                    </h2>

                                    {/* Description */}
                                    <p className="text-gray-600 text-lg leading-relaxed mb-10 max-w-md">
                                        Answers questions instantly, engages visitors proactively,
                                        captures emails naturally, and suggests demos at the perfect
                                        time.
                                    </p>

                                    {/* AI Sections Buttons - Minimal Cards Style */}
                                    <AISections
                                        config={currentConfig}
                                        onQuestionClick={handleQuestionClick}
                                        showShimmer={true}
                                        staggerDelay={100}
                                        language={selectedLanguage}
                                        siteName={selectedClient.siteName}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Unhide Parent Demo */}
                        {selectedConfigKey === 'sample:unhide-parent' && (
                            <div className="mb-8">
                                <div className="mb-2 text-sm text-gray-500">
                                    The section below is wrapped in a hidden parent (display: none).
                                    When the bar is visible, the parent unhides. Toggle the bar with
                                    the eye button above.
                                </div>
                                {/* Hidden wrapper - simulates client hiding a section */}
                                <div
                                    ref={unhideParentRef}
                                    style={{ display: 'none' }}
                                    className="rounded-lg shadow overflow-hidden"
                                >
                                    <div className="bg-gray-100 px-6 py-3 border-b">
                                        <h3 className="text-sm font-medium text-gray-700">
                                            Preview (unhideParent demo - this wrapper starts hidden)
                                        </h3>
                                    </div>
                                    <div
                                        className="p-6"
                                        style={{
                                            background:
                                                'linear-gradient(135deg, #D3FCF2 0%, #E8FDF7 100%)',
                                        }}
                                    >
                                        <AISections
                                            config={currentConfig}
                                            onQuestionClick={handleQuestionClick}
                                            onExpand={() => {
                                                if (unhideParentRef.current) {
                                                    unhideParentRef.current.style.display = '';
                                                }
                                            }}
                                            onCollapse={() => {
                                                if (unhideParentRef.current) {
                                                    unhideParentRef.current.style.display = 'none';
                                                }
                                            }}
                                            showShimmer={true}
                                            staggerDelay={100}
                                            language={selectedLanguage}
                                            siteName={selectedClient.siteName}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Demo Section (hidden for minimal-cards and unhide-parent since they have their own demos) */}
                        {selectedConfigKey !== 'sample:minimal-cards' &&
                            selectedConfigKey !== 'sample:unhide-parent' && (
                                <div className="mb-8 rounded-lg shadow overflow-hidden">
                                    <div className="bg-gray-100 px-6 py-3 border-b">
                                        <h3 className="text-sm font-medium text-gray-700">
                                            Preview
                                        </h3>
                                    </div>
                                    <div
                                        className="p-6"
                                        style={{
                                            // Use neutral background when customCSS is present (let CSS control it)
                                            background: currentConfig.customCSS
                                                ? '#f5f5f5'
                                                : 'linear-gradient(135deg, #D3FCF2 0%, #E8FDF7 100%)',
                                        }}
                                    >
                                        <AISections
                                            config={currentConfig}
                                            onQuestionClick={handleQuestionClick}
                                            showShimmer={true}
                                            staggerDelay={100}
                                            language={selectedLanguage}
                                            siteName={selectedClient.siteName}
                                        />
                                    </div>
                                </div>
                            )}

                        {/* Event Log */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="bg-gray-100 px-6 py-3 border-b flex justify-between items-center">
                                <h3 className="text-sm font-medium text-gray-700">
                                    Question Click Log
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setClickedQuestions([])}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="p-4 max-h-48 overflow-y-auto font-mono text-sm">
                                {clickedQuestions.length === 0 ? (
                                    <p className="text-gray-400 italic">
                                        Click a question to see it logged here...
                                    </p>
                                ) : (
                                    <ul className="space-y-1">
                                        {clickedQuestions.map((q) => (
                                            <li key={q} className="text-gray-700">
                                                {q}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* Configuration Preview */}
                        <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
                            <div className="bg-gray-100 px-6 py-3 border-b flex items-center justify-between">
                                <h3 className="text-sm font-medium text-gray-700">
                                    Current Configuration
                                </h3>
                                {currentOption && (
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded-full ${
                                            currentOption.source === 'database'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}
                                    >
                                        {currentOption.source === 'database'
                                            ? 'Database'
                                            : 'Sample'}
                                    </span>
                                )}
                            </div>
                            <pre className="p-4 text-xs overflow-x-auto bg-gray-50">
                                {JSON.stringify(currentConfig, null, 2)}
                            </pre>
                        </div>

                        {/* Instructions */}
                        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                                Testing Notes
                            </h3>
                            <ul className="text-blue-800 space-y-2 text-sm">
                                <li>
                                    • The AI Sections component automatically detects when the Rose
                                    widget is ready
                                </li>
                                <li>
                                    • Sections expand with animation when the widget becomes
                                    available
                                </li>
                                <li>
                                    • Clicking a question will trigger the widget and send the
                                    question
                                </li>
                                <li>
                                    • If the widget is not loaded, sections will remain collapsed
                                </li>
                                <li>
                                    • The shimmer effect plays once when buttons first appear
                                    (refresh to see again)
                                </li>
                            </ul>
                        </div>

                        {/* Spacer for scroll testing (hidesMainBar feature) */}
                        <div className="mt-8 space-y-8">
                            {Array.from({ length: 15 }, (_, i) => (
                                <div
                                    // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder sections
                                    key={i}
                                    className="bg-white rounded-lg shadow p-8 border border-gray-100"
                                >
                                    <h3 className="text-lg font-semibold text-gray-300 mb-3">
                                        Section {i + 1}
                                    </h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        Scroll down to test the hidesMainBar feature. When the AI
                                        Section search bar scrolls out of view, the main widget bar
                                        should reappear at the bottom.
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Rose Widget */}
                    <div ref={widgetContainerRef}>
                        <DisplayController
                            key={key}
                            apiUrl={currentApiUrl}
                            apiKey={getConfig().apiKey}
                            siteName={selectedClient.siteName}
                            domain={selectedClient.domain}
                            storageAdapter={storageAdapter}
                            networkAdapter={networkAdapter}
                            forceDisplay={true}
                            conversationLanguage={selectedLanguage}
                            visitorIp={getConfig().visitorIp}
                        />
                    </div>

                    {/* Configuration Panel */}
                    <ConfigPanel
                        isOpen={showConfig}
                        onClose={() => setShowConfig(false)}
                        onConfigChange={handleConfigChange}
                        apiHealthStatus={{}}
                        configSummary={getConfigSummary()}
                        onRefreshHealthChecks={() => Promise.resolve()}
                        isRefreshingHealth={false}
                    />
                </div>
            </AnalyticsProvider>
        </ClientConfigProvider>
    );
}
